<script lang="ts">
  import { onDestroy, onMount, untrack } from 'svelte'
  import { CityScene, type ViewState } from '$lib/renderer'
  import { navigate } from '$lib/router'
  import * as plansStore from '$lib/storage/plans'
  import * as settingsStore from '$lib/storage/settings'
  import { activeTool, resetActiveTool, type ActiveTool } from '$lib/editor'
  import { currentTheme } from '$lib/theme'
  import { parseImport } from '../import/parse'
  import { downloadImportJson } from '../city-view/export-json'
  import Toolbar from '../city-view/Toolbar.svelte'
  import { refreshCityList } from '../city-list'
  import EditorPanel from './EditorPanel.svelte'
  import HoverTooltip from './HoverTooltip.svelte'
  import SelectionPanel from './SelectionPanel.svelte'
  import ValidationBanner from './ValidationBanner.svelte'
  import {
    closeEditor,
    dispatch,
    editorImport,
    editorPlan,
    editorState,
    hoveredBuildingId,
    openPlan,
    redo,
    reloadPlan,
    replacePlan,
    selectedBuildingId,
    undo,
    undoCanRedo,
    undoCanUndo,
    validation,
  } from './stores'
  import { dragState } from './drag-state'
  import { makeStoreHandlers } from './store-tool-handlers'
  import { makeRoadHandlers } from './road-tool-handlers'
  import { extractPlayerEra, resolveBuilding } from '$lib/catalogue'
  import { computeFootprint } from '$lib/stats/footprint'
  import { getProduction } from '$lib/stats'
  import { get } from 'svelte/store'
  import type { GridCoord, InventoryItem, ResolvedBuilding } from '$lib/types'

  interface Props {
    planId: number
  }

  const { planId }: Props = $props()

  let host: HTMLDivElement | undefined = $state()
  let scene: CityScene | null = null
  let cycleIndex = 0

  /** Cursor position relative to the canvas host, used to anchor the
   *  HoverTooltip. Null when the cursor isn't over the canvas. The
   *  tooltip itself reads `hoveredBuildingId` independently — both must
   *  be set for the tooltip to render. */
  let hoverCursor: { x: number; y: number } | null = $state(null)

  function onCanvasMove(ev: PointerEvent) {
    if (!host) return
    const rect = host.getBoundingClientRect()
    hoverCursor = { x: ev.clientX - rect.left, y: ev.clientY - rect.top }
  }

  function onCanvasLeave() {
    hoverCursor = null
  }

  /** View-state persistence: pan/zoom fires per-pixel during drag, so we
   *  debounce 500ms to avoid hammering Dexie. Selection persists
   *  immediately because the change is rare and the user notices the lag.
   *  Both writes share the same Settings.lastViewState blob, so the latest
   *  selection rides on top of the latest camera. */
  let pendingViewState: ViewState | null = null
  let viewSaveTimer: ReturnType<typeof setTimeout> | null = null

  function scheduleViewSave(state: ViewState) {
    pendingViewState = state
    if (viewSaveTimer !== null) return
    viewSaveTimer = setTimeout(() => {
      viewSaveTimer = null
      const s = pendingViewState
      pendingViewState = null
      if (s) void persistViewState(s, $selectedBuildingId)
    }, 500)
  }

  async function persistViewState(state: ViewState, selectedId: number | null) {
    void settingsStore.setLastViewState({
      planId,
      zoom: state.zoom,
      panX: state.panX,
      panY: state.panY,
      ...(selectedId !== null ? { selectedBuildingId: selectedId } : {}),
    })
  }

  /** Toolbar footprint summary. Null when no plan is loaded; recomputes
   *  on every plan / import change. resolveBuilding is memoised per
   *  (importId, cityentityId) so the per-redraw cost is just a Map lookup
   *  per building. */
  const toolbarStats = $derived.by(() => {
    const plan = $editorPlan
    if (!plan) return null
    const importedCity = $editorImport
    return computeFootprint(plan, (id) => resolveBuilding(id, importedCity ?? undefined))
  })

  /** Phase 4 production totals. Reaches for the memoised wrapper in
   *  `lib/stats`; the cache is keyed by (planId, version) so consecutive
   *  reads inside one render don't recompute. Validation supplies the set
   *  of invalid building ids — disconnected, outside-area and overlapping
   *  buildings contribute zero, matching the spec's strict-validation
   *  contract. */
  const toolbarProduction = $derived.by(() => {
    const plan = $editorPlan
    if (!plan) return null
    const importedCity = $editorImport
    const validationResult = $validation
    const era = extractPlayerEra(importedCity ?? undefined)
    return getProduction(
      plan,
      (id) => resolveBuilding(id, importedCity ?? undefined),
      validationResult.invalidBuildingIds,
      era,
    )
  })

  // Modifier-hold state: holding R or S temporarily switches to that tool.
  // When the key releases, the previously-active tool is restored. Only one
  // modifier may be active at a time — pressing S while holding R does
  // nothing until R releases.
  let activeModifier: 'R' | 'S' | null = null
  let modifierStashedTool: ActiveTool | null = null
  /** What the modifier set the active tool to. Used on keyup to detect
   *  whether the user explicitly overrode the modifier (e.g. clicked a
   *  toolbar button) and to suppress the restore in that case. */
  let modifierSetTool: ActiveTool | null = null

  onMount(async () => {
    // A previous editor session might have left the activeTool store on
    // 'road' or 'store'; reset before mounting so the new editor opens
    // in select mode regardless.
    resetActiveTool()
    await openPlan(planId)
    if (!host) return
    scene = await CityScene.create(host, {
      onSelect: (id) => selectedBuildingId.set(id),
      onHover: (id) => hoveredBuildingId.set(id),
      onMove: handleMove,
      onPlacementDrop: handlePlacementDrop,
      onPlacementCancel: cancelPickup,
      onViewChange: scheduleViewSave,
    })
    scene.setExternalToolHandlers({
      store: makeStoreHandlers({
        commitSweep: handleStoreSweep,
        markBuildingsForStore: (ids) => scene?.markBuildingsForStore(ids),
        clearStoreMarks: () => scene?.clearStoreMarks(),
      }),
      road: makeRoadHandlers({
        getPlan: () => get(editorPlan),
        getImportedCity: () => get(editorImport),
        commitStroke: handleRoadStroke,
        drawStrokePreview: (squares) => scene?.drawStrokePreview(squares),
        clearStrokePreview: () => scene?.clearStrokePreview(),
      }),
    })
    pushSceneInputs()

    // Restore the previous camera + selection if they belong to this plan;
    // otherwise fit-to-screen so the new plan is fully visible. The
    // selection restore runs after the scene is wired so the canvas redraws
    // with the selection outline on first paint.
    const savedSettings = await settingsStore.get()
    const lastView = savedSettings.lastViewState
    const restored = lastView && lastView.planId === planId
      ? scene.applyViewState({ zoom: lastView.zoom, panX: lastView.panX, panY: lastView.panY })
      : false
    if (!restored) scene.fitToScreen()
    if (restored && lastView && lastView.selectedBuildingId !== undefined) {
      selectedBuildingId.set(lastView.selectedBuildingId)
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onWindowBlur)
  })

  onDestroy(() => {
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('keyup', onKeyUp)
    window.removeEventListener('blur', onWindowBlur)
    // Flush any in-flight view-state save so a fast navigate-away doesn't
    // strand a dropped pan position on the floor.
    if (viewSaveTimer !== null) {
      clearTimeout(viewSaveTimer)
      viewSaveTimer = null
      const s = pendingViewState
      pendingViewState = null
      if (s) void persistViewState(s, $selectedBuildingId)
    }
    scene?.destroy()
    scene = null
    resetActiveTool()
    void closeEditor()
  })

  // Re-push scene inputs whenever the plan, the imported city, the selection
  // or the validation result change.
  //
  // The previous version of this effect used `void $editorPlan` etc. to
  // establish dependencies. That pattern was unreliable: Svelte 5 treats a
  // `void X` expression as a discarded value and the dependency on
  // $editorPlan was not always picked up — Ctrl+D's duplicate command would
  // mutate the store but the effect never re-fired, so the canvas didn't
  // repaint. Reading the values into local consts makes the dependency
  // unambiguous, and using those consts inside `untrack` keeps the scene
  // call from re-subscribing.
  $effect(() => {
    const plan = $editorPlan
    const importedCity = $editorImport
    const selectedId = $selectedBuildingId
    const validationResult = $validation
    untrack(() => {
      if (!scene || !plan || !importedCity) return
      scene.update({
        plan,
        importedCity,
        selectedId,
        invalidIds: validationResult.invalidBuildingIds,
      })
    })
  })

  // CSS panels retint via the body-class flip; the Pixi canvas needs an
  // explicit redraw so it can re-read every layer's colours from the new
  // variable values. Same pattern as the Phase 2 duplicate-render fix —
  // the read-into-const idiom keeps the dependency unambiguous.
  $effect(() => {
    const theme = $currentTheme
    untrack(() => {
      if (scene && theme) scene.applyTheme()
    })
  })

  // Forward tool changes from the editor's store into the Pixi scene's
  // pointer dispatcher. Read-into-const + untrack matches the theme effect
  // and the plan effect — Svelte 5's effect tracker only registers reads
  // that happen as named expressions, never `void $store`.
  $effect(() => {
    const tool = $activeTool
    untrack(() => {
      scene?.setActiveTool(tool)
    })
  })

  /** Persist selection alongside the latest view state. Debounce shares the
   *  pan/zoom timer so a click-then-pan only writes once. Read-into-const
   *  matches the theme/plan effects so Svelte 5 picks up the dependency. */
  $effect(() => {
    const selectedId = $selectedBuildingId
    untrack(() => {
      if (!scene) return
      // The selection write reuses the debounce — schedule a save with the
      // current view, and the timer's payload picks up the new selection
      // (which has already landed in the store by the time the timer fires).
      void selectedId
      scheduleViewSave(scene.getViewState())
    })
  })

  /** Synchronous repaint helper. Used by `dispatchAndRepaint` (so handlers
   *  never depend on the $effect timing) and by the initial onMount push. */
  function pushSceneInputs() {
    if (!scene) return
    const plan = $editorPlan
    const importedCity = $editorImport
    if (!plan || !importedCity) return
    scene.update({
      plan,
      importedCity,
      selectedId: $selectedBuildingId,
      invalidIds: $validation.invalidBuildingIds,
    })
  }

  /** Wrap dispatch + repaint so every command site is guaranteed to update
   *  the canvas synchronously. The $effect above is the structural path; this
   *  is belt-and-braces — if any future command kind exposes the same effect-
   *  timing bug Ctrl+D hit, the canvas still reflects the latest plan. */
  function dispatchAndRepaint(command: import('$lib/editor').Command) {
    const result = dispatch(command)
    pushSceneInputs()
    return result
  }

  function undoAndRepaint() {
    undo()
    pushSceneInputs()
  }

  function redoAndRepaint() {
    redo()
    pushSceneInputs()
  }

  function modifierForKey(key: string): { tool: ActiveTool; mod: 'R' | 'S' } | null {
    const k = key.toLowerCase()
    if (k === 'r') return { tool: 'road', mod: 'R' }
    if (k === 's') return { tool: 'store', mod: 'S' }
    return null
  }

  function onKeyDown(ev: KeyboardEvent) {
    const t = ev.target as HTMLElement | null
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return

    // Modifier-hold (R / S). First keydown of a hold only — auto-repeat is
    // ignored via ev.repeat. Skipped when any system modifier is held so
    // Ctrl+R for browser refresh and friends still work. Only one modifier
    // session is active at a time.
    if (
      !ev.repeat &&
      !ev.ctrlKey &&
      !ev.metaKey &&
      !ev.altKey &&
      !ev.shiftKey &&
      activeModifier === null
    ) {
      const m = modifierForKey(ev.key)
      if (m) {
        ev.preventDefault()
        activeModifier = m.mod
        modifierStashedTool = $activeTool
        modifierSetTool = m.tool
        activeTool.set(m.tool)
        return
      }
    }

    const cmdOrCtrl = ev.ctrlKey || ev.metaKey

    // Undo / redo. Ctrl+Y is the Windows redo alternative.
    if (cmdOrCtrl && !ev.shiftKey && ev.key.toLowerCase() === 'z') {
      ev.preventDefault()
      undoAndRepaint()
      return
    }
    if (
      cmdOrCtrl &&
      ((ev.shiftKey && ev.key.toLowerCase() === 'z') || ev.key.toLowerCase() === 'y')
    ) {
      ev.preventDefault()
      redoAndRepaint()
      return
    }

    // Duplicate selected. Ctrl+D in the browser triggers Add Bookmark — the
    // preventDefault is essential.
    if (cmdOrCtrl && ev.key.toLowerCase() === 'd') {
      ev.preventDefault()
      duplicateSelected()
      return
    }

    // Delete / Backspace removes the selected building to inventory.
    if ((ev.key === 'Delete' || ev.key === 'Backspace') && $selectedBuildingId !== null) {
      ev.preventDefault()
      removeSelected()
      return
    }

    // Arrow nudges. Shift multiplies by 4 (one half-row of small buildings).
    // Each press is its own command so undo unwinds nudge-by-nudge — that's
    // the v1 contract; coalescing is a Phase 7 polish item.
    const step = ev.shiftKey ? 4 : 1
    if (ev.key === 'ArrowLeft') {
      ev.preventDefault()
      nudgeSelected(-step, 0)
      return
    }
    if (ev.key === 'ArrowRight') {
      ev.preventDefault()
      nudgeSelected(step, 0)
      return
    }
    if (ev.key === 'ArrowUp') {
      ev.preventDefault()
      nudgeSelected(0, -step)
      return
    }
    if (ev.key === 'ArrowDown') {
      ev.preventDefault()
      nudgeSelected(0, step)
      return
    }

    if (ev.key === 'Escape') {
      handleEscape()
    } else if (ev.key === 'f' || ev.key === 'F') scene?.fitToScreen()
    else if (ev.key === '+' || ev.key === '=') scene?.zoomIn()
    else if (ev.key === '-' || ev.key === '_') scene?.zoomOut()
  }

  /**
   * Modifier-hold release. Doesn't gate on input focus — releasing the key
   * always ends the modifier session, even if the user is typing in a field
   * (we don't want a "stuck on road" state if focus shifted mid-hold).
   *
   * If the active tool no longer matches what the modifier set, the user
   * explicitly switched (e.g. clicked a toolbar button) during the hold;
   * respect that and don't restore the stash.
   */
  function onKeyUp(ev: KeyboardEvent) {
    const m = modifierForKey(ev.key)
    if (!m || activeModifier !== m.mod) return
    if ($activeTool === modifierSetTool && modifierStashedTool !== null) {
      activeTool.set(modifierStashedTool)
    }
    activeModifier = null
    modifierStashedTool = null
    modifierSetTool = null
  }

  /**
   * Window blur safety net. If the user Tab-aways or focus-shifts while
   * holding a modifier, the keyup may never fire and we'd be stuck on the
   * temporary tool. Treat blur the same as keyup: restore stashed tool if
   * the modifier set tool is still active.
   */
  function onWindowBlur() {
    if (activeModifier === null) return
    if ($activeTool === modifierSetTool && modifierStashedTool !== null) {
      activeTool.set(modifierStashedTool)
    }
    activeModifier = null
    modifierStashedTool = null
    modifierSetTool = null
  }

  /**
   * Esc cascade. Order matters per the Phase 2.5 spec:
   *   1. Mid-pickup → cancel pickup (existing Phase 2 behaviour).
   *   2. Mid Pixi-internal gesture (pan, drag-to-move) → cancel; stay on tool.
   *   3. On a non-select tool → return to select.
   *   4. On select with a selection → clear selection.
   *   5. On select with nothing selected → no-op.
   * Each step exits early so a single Esc only does one thing.
   */
  function handleEscape() {
    if ($dragState.kind !== 'idle') {
      cancelPickup()
      return
    }
    if (scene?.cancelInFlightGesture()) return
    if ($activeTool !== 'select') {
      activeTool.set('select')
      return
    }
    if ($selectedBuildingId !== null) {
      selectedBuildingId.set(null)
    }
  }

  function nudgeSelected(dx: number, dy: number) {
    const id = $selectedBuildingId
    const plan = $editorPlan
    if (id === null || !plan) return
    const b = plan.buildings.find((x) => x.id === id)
    if (!b) return
    dispatchAndRepaint({
      kind: 'move',
      id,
      from: { x: b.x, y: b.y },
      to: { x: b.x + dx, y: b.y + dy },
    })
  }

  async function rename(name: string) {
    const plan = $editorPlan
    if (!plan) return
    await plansStore.rename(plan.id, name)
    await reloadPlan()
    await refreshCityList()
  }

  async function resetToImport() {
    const plan = $editorPlan
    const importedCity = $editorImport
    if (!plan || !importedCity) return
    if (
      !confirm(
        'Reset city to the original layout? This clears your undo history.',
      )
    )
      return
    const re = parseImport(importedCity.rawJson, plan.createdAt)
    if (!re.ok) {
      alert(`Couldn't reset: ${re.message}`)
      return
    }
    await replacePlan({
      buildings: re.initialPlan.buildings,
      inventory: re.initialPlan.inventory,
      unlockedAreas: re.initialPlan.unlockedAreas,
      notes: re.initialPlan.notes,
      nextEntityId: re.initialPlan.nextEntityId,
    })
    await refreshCityList()
    scene?.fitToScreen()
  }

  function exportJson() {
    const importedCity = $editorImport
    if (!importedCity) return
    void downloadImportJson(importedCity)
  }

  /**
   * Build the [show]-button cycle order. Per the Phase 3 spec:
   *   1. All overlap-error buildings, in id order.
   *   2. All outside-area buildings, in id order.
   *   3. Disconnected buildings, grouped by cluster — each disconnected
   *      ValidationError is one cluster; cluster order matches the error
   *      array's order (already id-sorted by lowest building per cluster
   *      in connectivity.ts), and ids within each cluster are id-sorted.
   * Buildings are deduplicated across categories: a building flagged
   * overlap AND disconnected appears once, in the overlap segment.
   */
  function buildCycleOrder(): number[] {
    const result = $validation
    const ordered: number[] = []
    const seen = new Set<number>()
    const collect = (codeFilter: (code: string) => boolean) => {
      const ids: number[] = []
      for (const e of result.errors) {
        if (!codeFilter(e.code)) continue
        for (const id of e.buildingIds) ids.push(id)
      }
      const sorted = Array.from(new Set(ids)).sort((a, b) => a - b)
      for (const id of sorted) {
        if (!seen.has(id)) {
          seen.add(id)
          ordered.push(id)
        }
      }
    }
    collect((c) => c === 'overlap')
    collect((c) => c === 'outside-unlocked-area')
    // Disconnected — preserve cluster grouping (don't merge across clusters).
    for (const e of result.errors) {
      if (e.code !== 'disconnected') continue
      const sorted = [...e.buildingIds].sort((a, b) => a - b)
      for (const id of sorted) {
        if (!seen.has(id)) {
          seen.add(id)
          ordered.push(id)
        }
      }
    }
    return ordered
  }

  // Selection-cycling button on the validation banner. Each click selects
  // the next offending building and pans the canvas to it (zoom preserved).
  function cycleErrors() {
    const plan = $editorPlan
    if (!plan) return
    const ids = buildCycleOrder()
    if (ids.length === 0) return
    cycleIndex = cycleIndex % ids.length
    const id = ids[cycleIndex]
    cycleIndex = (cycleIndex + 1) % ids.length
    selectedBuildingId.set(id)
    const b = plan.buildings.find((x) => x.id === id)
    if (b) scene?.centerOn(b.x, b.y)
  }

  function removeSelected() {
    const id = $selectedBuildingId
    if (id === null) return
    dispatchAndRepaint({ kind: 'remove', id })
    selectedBuildingId.set(null)
  }

  function duplicateSelected() {
    const id = $selectedBuildingId
    const plan = $editorPlan
    if (id === null || !plan) return
    const source = plan.buildings.find((b) => b.id === id)
    if (!source) return
    const resolved = resolveBuilding(source.cityentityId, $editorImport ?? undefined)
    const width = resolved && resolved.width > 0 ? resolved.width : 1
    const result = dispatchAndRepaint({
      kind: 'duplicate',
      sourceId: id,
      at: { x: source.x + width, y: source.y },
    })
    if (result) {
      const newId = result.newPlan.buildings[result.newPlan.buildings.length - 1]?.id
      if (typeof newId === 'number') selectedBuildingId.set(newId)
    }
  }

  function handleMove(id: number, from: GridCoord, to: GridCoord) {
    dispatchAndRepaint({ kind: 'move', id, from, to })
  }

  /** Store-tool callback. The handler accumulated building ids during
   *  the drag; commit as one operation so undo wipes the whole sweep
   *  in a single Ctrl+Z. Single-id sweeps go through the existing
   *  `remove` so the undo stack reads cleanly when only one building
   *  was hit. Drops selection if the stored set included it (the right
   *  panel would otherwise show "Click a building..." anyway since the
   *  lookup fails). */
  function handleStoreSweep(buildingIds: number[]) {
    if (buildingIds.length === 0) return
    if (buildingIds.length === 1) {
      dispatchAndRepaint({ kind: 'remove', id: buildingIds[0] })
    } else {
      dispatchAndRepaint({ kind: 'removeManyToInventory', ids: buildingIds })
    }
    if (
      $selectedBuildingId !== null &&
      buildingIds.includes($selectedBuildingId)
    ) {
      selectedBuildingId.set(null)
    }
  }

  /**
   * Clear-all-buildings handler. Sends every placed building (including
   * GBs and roads) to inventory in one command — single Ctrl+Z restores.
   * Confirms before firing because the action is visually dramatic;
   * undo is available but the player still has to navigate back to it.
   */
  function clearAllBuildings() {
    const plan = $editorPlan
    if (!plan) return
    const ids = plan.buildings.map((b) => b.id)
    if (ids.length === 0) return
    if (
      !confirm(
        `Move all ${ids.length} placed buildings to inventory? You can undo with Ctrl+Z.`,
      )
    )
      return
    dispatchAndRepaint({ kind: 'removeManyToInventory', ids })
    selectedBuildingId.set(null)
  }

  /** Road-tool callback. The handler has already filtered the stroke to
   *  unique tiles that don't already contain a road; here we just commit
   *  it as a single placeMany so undo/redo treats the whole stroke as
   *  one operation. */
  function handleRoadStroke(
    placements: ReadonlyArray<{ cityentityId: string; at: GridCoord }>,
  ) {
    if (placements.length === 0) return
    dispatchAndRepaint({ kind: 'placeMany', placements: [...placements] })
  }

  function inventoryPickup(item: InventoryItem, resolved: ResolvedBuilding | null) {
    // Unknown footprints fall back to 1×1 so the ghost is at least visible
    // and the user can still drop the item somewhere reviewable.
    const width = resolved && resolved.width > 0 ? resolved.width : 1
    const height = resolved && resolved.height > 0 ? resolved.height : 1
    dragState.set({
      kind: 'placing-from-inventory',
      inventoryId: item.id,
      cityentityId: item.cityentityId,
      width,
      height,
      level: item.level,
      ghost: null,
    })
    scene?.beginPlacement({ width, height })
  }

  function inventoryDiscard(items: InventoryItem[], _label: string, action: 'one' | 'all') {
    // Confirmation lives in InventoryTab now (a proper <dialog> with
    // Remove 1 / Remove all buttons rather than the old confirm() flow).
    // We just dispatch the right command kind based on the action.
    if (items.length === 0) return
    if (action === 'one') {
      dispatchAndRepaint({ kind: 'discardFromInventory', inventoryId: items[0].id })
      return
    }
    // 'all' — bulk command undoes the entire stack as one operation.
    dispatchAndRepaint({
      kind: 'discardManyFromInventory',
      inventoryIds: items.map((i) => i.id),
    })
  }

  function palettePickup(resolved: ResolvedBuilding) {
    const width = resolved.width > 0 ? resolved.width : 1
    const height = resolved.height > 0 ? resolved.height : 1
    dragState.set({
      kind: 'placing-from-palette',
      cityentityId: resolved.id,
      width,
      height,
      level: resolved.type === 'greatbuilding' ? 1 : undefined,
      ghost: null,
    })
    scene?.beginPlacement({ width, height })
  }

  function cancelPickup() {
    dragState.set({ kind: 'idle' })
    scene?.cancelPlacement()
  }

  function handlePlacementDrop(at: GridCoord) {
    const state = $dragState
    if (state.kind === 'placing-from-inventory') {
      const result = dispatchAndRepaint({
        kind: 'place',
        source: { from: 'inventory', inventoryId: state.inventoryId },
        at,
      })
      finishPickup(result)
    } else if (state.kind === 'placing-from-palette') {
      const result = dispatchAndRepaint({
        kind: 'place',
        source: {
          from: 'palette',
          cityentityId: state.cityentityId,
          level: state.level,
        },
        at,
      })
      finishPickup(result)
    } else {
      // No active pickup — just clear the visual ghost defensively.
      scene?.cancelPlacement()
    }
  }

  function finishPickup(result: { newPlan: { buildings: { id: number }[] } } | null) {
    dragState.set({ kind: 'idle' })
    scene?.cancelPlacement()
    if (result) {
      const newId = result.newPlan.buildings[result.newPlan.buildings.length - 1]?.id
      if (typeof newId === 'number') selectedBuildingId.set(newId)
    }
  }
</script>

<div class="flex h-screen flex-col bg-bg-base text-text-primary">
  {#if $editorState.loading}
    <div class="flex h-full items-center justify-center text-text-secondary">Loading…</div>
  {:else if $editorState.error || !$editorPlan || !$editorImport}
    <div class="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
      <p class="text-status-neg">
        Couldn't open this city: {$editorState.error ?? 'not found'}
      </p>
      <button
        type="button"
        class="rounded bg-surface-3 px-3 py-1 text-sm text-chrome-text hover:bg-surface-2"
        onclick={() => navigate({ name: 'list' })}>Back to list</button
      >
    </div>
  {:else}
    <Toolbar
      plan={$editorPlan}
      stats={toolbarStats}
      production={toolbarProduction}
      onBack={() => navigate({ name: 'list' })}
      onRename={rename}
      onResetToImport={resetToImport}
      onExport={exportJson}
      onZoomIn={() => scene?.zoomIn()}
      onZoomOut={() => scene?.zoomOut()}
      onZoomFit={() => scene?.fitToScreen()}
      onZoomReset={() => scene?.zoomReset()}
      onUndo={undoAndRepaint}
      onRedo={redoAndRepaint}
      canUndo={$undoCanUndo}
      canRedo={$undoCanRedo}
    />
    <div class="relative flex-1 overflow-hidden">
      <!-- Pixi canvas container. The pointer handlers track cursor position
           for the hover tooltip; the canvas inside it owns its own input
           pipeline. role="presentation" silences the a11y warning — keyboard
           navigation and selection happen via the toolbar/panels, not via
           this div. -->
      <div
        bind:this={host}
        class="absolute inset-0"
        onpointermove={onCanvasMove}
        onpointerleave={onCanvasLeave}
        role="presentation"
      ></div>
      <HoverTooltip
        plan={$editorPlan}
        importedCity={$editorImport}
        hoveredId={$hoveredBuildingId}
        cursor={hoverCursor}
      />
      <ValidationBanner result={$validation} onCycle={cycleErrors} />
      {#if $selectedBuildingId !== null}
        <SelectionPanel
          plan={$editorPlan}
          importedCity={$editorImport}
          selectedId={$selectedBuildingId}
          onClose={() => selectedBuildingId.set(null)}
          onRemoveSelected={removeSelected}
          onDuplicateSelected={duplicateSelected}
        />
      {/if}
      <EditorPanel
        plan={$editorPlan}
        importedCity={$editorImport}
        onInventoryPickup={inventoryPickup}
        onInventoryDiscard={inventoryDiscard}
        onPalettePickup={palettePickup}
        onClearAllBuildings={clearAllBuildings}
      />
    </div>
  {/if}
</div>
